/**
 * PropertyImportPage — route `/properties/import` (PMS portal).
 *
 * SCRUM-181 (P5) — Bulk import UI shell.
 *
 * Per `~/Desktop/design_handoff_properties/PROPERTIES_LIST_SPEC.md` §2,
 * the Properties list header carries a secondary "↑ Import CSV"
 * action. SCRUM-181 lands the destination route with the bundle's
 * upload-dropzone + queue UI layout, all shell — the BE has no import
 * endpoint today.
 *
 * The dropzone + queue table chrome render so a contributor reading
 * the placeholder sees the eventual layout; the body is wrapped in
 * `<NotConnectedYet>` until the BE grows the upload endpoint
 * (separate SCRUM ticket — out of scope for this epic).
 *
 * Gated by `property.write` at the route level.
 */

import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Icon } from '@sparta/ui-kit';
import { NotConnectedYet } from '../../../entities/not-connected-yet/index.js';

export function PropertyImportPage(): React.ReactElement {
  const navigate = useNavigate();
  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-12 sm:py-8">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/properties')}
        className="mb-3"
      >
        <Icon name="chevLeft" size={14} aria-hidden />
        <span className="ml-2">Properties</span>
      </Button>

      <header className="mb-6">
        <p
          className="text-xs font-bold uppercase mb-1"
          style={{
            color: 'var(--fg-tertiary)',
            letterSpacing: '0.12em',
          }}
        >
          Inventory
        </p>
        <h1
          className="text-2xl font-semibold m-0"
          style={{
            color: 'var(--fg-primary)',
            letterSpacing: '-0.025em',
          }}
        >
          Import properties
        </h1>
        <p
          className="mt-1.5 text-sm m-0"
          style={{ color: 'var(--fg-secondary)' }}
        >
          Upload a CSV with one row per property. The first column is
          the property name; the remaining columns map to the create
          wizard's fields.
        </p>
      </header>

      <Card padding="lg" className="mb-4">
        <div
          // Upload dropzone — visually a dashed-border drop target.
          // No real upload handler today; the inner body is a
          // NotConnectedYet placeholder.
          className="rounded-xl p-8 flex flex-col items-center justify-center"
          style={{
            border: '2px dashed var(--border-default)',
            background: 'var(--bg-sunken)',
          }}
        >
          <Icon
            name="arrowUp"
            size={32}
            aria-hidden
            style={{ color: 'var(--fg-tertiary)' }}
          />
          <p
            className="mt-2 mb-1 text-sm font-semibold"
            style={{ color: 'var(--fg-primary)' }}
          >
            Drop a CSV here
          </p>
          <p
            className="m-0 mb-3 text-xs"
            style={{ color: 'var(--fg-tertiary)' }}
          >
            or click to browse
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              alert(
                'Bulk import — not connected yet. Tracked under SCRUM-181 (P5).',
              );
            }}
          >
            Choose file
          </Button>
        </div>
      </Card>

      <NotConnectedYet
        surface="Bulk import queue"
        ticket="SCRUM-181"
      />
    </div>
  );
}
